import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import CTASection from "@/components/CTASection";
import { Reveal, SectionHead } from "@/components/Reveal";
import { CAPABILITIES, WHY, IMG } from "@/data/site";

const WHY_TEXT = {
  award: "Over 20 Years of Experience",
  home: "Family-Owned and Operated",
  map: "Free Estimates Within 100 Miles",
  truck: "Mobile Welding Service",
};

export default function About() {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="Your trusted partner for custom metal fabrication and mobile welding repairs"
        lead="Park and Spark Mobile Welding, LLC — a family-owned mobile welding company based in Hardin, MT."
      />

      <section className="py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <SectionHead eyebrow="Who We Are" title="Expertise that comes to your location" />
            <Reveal delay={0.1}>
              <p className="text-slate-400 mt-6 leading-relaxed">
                We have over 20 years of experience in the metal fabrication industry. Our
                expertise includes fabricating and welding wrought iron fences, handrails, stairs,
                gates, horse fences, and corrals. On the commercial side, we excel in repairing
                and hard-facing construction equipment.
              </p>
              <p className="text-slate-400 mt-4 leading-relaxed">
                We accommodate large and small jobs and enjoy supporting our customers with
                emergency fabrication and welding repairs. We bring our expertise and equipment to
                your location for your convenience and peace of mind.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <h3 className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-gold mt-10">
                What We Build &amp; Repair
              </h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {CAPABILITIES.map((c) => (
                  <span
                    key={c}
                    className="border border-line bg-coal px-3 py-1.5 text-xs font-medium uppercase tracking-wider text-slate-300"
                  >
                    {c}
                  </span>
                ))}
              </div>
              <Link
                to="/contact"
                data-testid="about-page-cta-button"
                className="mt-10 inline-flex items-center gap-2 bg-gold px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-ink hover:bg-gold-light transition-colors"
              >
                Request a Free Estimate
                <ArrowRight size={15} aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.1} className="grid grid-cols-2 gap-4">
            <img
              src={IMG("img_2030.jpg")}
              alt="Park & Spark welder fabricating steel"
              loading="lazy"
              className="w-full aspect-[3/4] object-cover border border-line"
            />
            <img
              src={IMG("img_3768_c1.jpg")}
              alt="Carport structure build at sunset"
              loading="lazy"
              className="w-full aspect-[3/4] object-cover border border-line mt-8"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-coal border-y border-line py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Why Choose Us" title="Why Park & Spark" />
          <div className="mt-12 grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07} className="h-full">
                <div className="h-full bg-coal p-7">
                  <span className="font-mono text-xs text-gold tracking-[0.25em]">0{i + 1}</span>
                  <h3 className="font-display text-lg font-bold uppercase text-slate-100 mt-3 leading-snug">
                    {WHY_TEXT[w.icon]}
                  </h3>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
